
import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../apiConfig';
import Header from "../Components/Common/Header";
import Pagination from '../Components/Common/Pagination';
import { UserCog, AlertCircle, CheckCircle, Loader, Search } from "lucide-react";
import { useNavigate } from 'react-router-dom';
import '../Styles/ManageRolesPage.css';

const ManageRolesPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 5;
  const [totalPages, setTotalPages] = useState(1);

  const fetchUsers = useCallback(async (page) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('idToken');
      const companyId = localStorage.getItem('companyId');

      if (!companyId) {
        setError("No company selected");
        setLoading(false);
        return;
      }

      const response = await fetch(
        `${API_BASE_URL}/admin/role-management?companyId=${companyId}&page=${page}&limit=${usersPerPage}${searchTerm ? `&search=${searchTerm}` : ''}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch users');
      if (response.status === 401 || response.status === 403) {
        localStorage.clear();
        window.location.href = '/';
        return;
      }
      const data = await response.json();

      setUsers(data.users || []);
      setFilteredUsers(data.users || []);
      setTotalPages(data.totalPages || 1);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching users:", err);
      setError(err.message);
      setLoading(false);
    }
  }, [searchTerm, usersPerPage]);

  useEffect(() => {
    const token = localStorage.getItem('idToken');
    if (!token) {
      navigate('/');
      return;
    }

    fetchUsers(currentPage);
  }, [currentPage, navigate, fetchUsers]);

  // Handle search input changes
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle search submission
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page when searching
    fetchUsers(1);
  };

  const makeAdmin = async (userId) => {
    try {
      const token = localStorage.getItem('idToken');
      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/promote`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setUsers(users.filter(user => user.id !== userId));
        setFilteredUsers(filteredUsers.filter(user => user.id !== userId));
        setSuccessMessage('User promoted to admin successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        throw new Error(data.message || 'Failed to promote user');
      }
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(null), 3000);
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  return (
    <div className="mrp-container">
      <div className="ap-wrap-content">
        <Header 
          title="Manage User Roles" 
          description="Promote users to admin role" 
          icon={<UserCog size={28} color="#f3f4f6" />}
        />
      
        <div className="mrp-content">
          {/* Search Bar */}
          <div className="mrp-search-container">
            <form onSubmit={handleSearch} className="mrp-search-form">
              <div className="mrp-search-input-wrapper">
                <Search size={18} className="mrp-search-icon" />
                <input
                  type="text"
                  placeholder="Search users by name or email..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="mrp-search-input"
                />
              </div>
              <button type="submit" className="mrp-search-button">
                Search
              </button>
            </form>
          </div>

          {error && (
            <div className="mrp-error">
              <AlertCircle size={20} />
              <span>{error}</span>
            </div>
          )}

          {successMessage && (
            <div className="mrp-success">
              <CheckCircle size={20} />
              <span>{successMessage}</span>
            </div>
          )}

          {loading ? (
            <div className="mrp-loading">
              <Loader size={40} className="mrp-spinner" />
              <p style={{ marginTop: '16px' }}>Loading users...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="mrp-empty-state">
              <UserCog size={48} color="#6b7280" className="mrp-empty-icon" />
              <h3 className="mrp-empty-title">No Users to Promote</h3>
              <p className="mrp-empty-text">No regular users found that can be promoted to admin.</p>
            </div>
          ) : (
            <div className="mrp-table-card">
              <div className="mrp-table-wrapper">
                <table className="mrp-table">
                  <thead>
                    <tr>
                      <th className="mrp-table-header">Name</th>
                      <th className="mrp-table-header">Email</th>
                      <th className="mrp-table-header">Current Role</th>
                      <th className="mrp-table-header">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user, index) => (
                      <tr 
                        key={user.id} 
                        className={`mrp-table-row ${index % 2 === 0 ? 'mrp-table-row-even' : ''}`}
                      >
                        <td className="mrp-name-cell">
                          <div className="mrp-user-avatar">
                            {user.name?.charAt(0).toUpperCase() || "?"}
                          </div>
                          {user.name}
                        </td>
                        <td className="mrp-table-cell">{user.email}</td>
                        <td className="mrp-table-cell">
                          <span className="mrp-role-badge">{user.role}</span>
                        </td>
                        <td className="mrp-table-cell">
                          <button
                            onClick={() => makeAdmin(user.id)}
                            className="mrp-promote-btn"
                          >
                            Make Admin
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  className="mrp-pagination"
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageRolesPage;
